import { useState, useRef, useEffect } from "react";
import type { KeyboardEvent, RefObject } from "react";
import { cn } from "@/lib/utils";
import {
  arrowKeyToDirection,
  findNavTarget,
  isPrintableKey,
  type NavDirection,
} from "./nav-helpers";

interface BaseProps {
  className?: string;
  align?: "left" | "right";
  disabled?: boolean;
  /** Brukes til vertikal piltast-navigasjon — celler med samme `dataCol` mappes til samme kolonne. */
  dataCol?: string;
}

type EditableCellProps =
  | (BaseProps & {
      value: string;
      type: "text" | "date";
      onSave: (newValue: string) => Promise<void> | void;
    })
  | (BaseProps & {
      value: number;
      type: "number";
      step?: number;
      onSave: (newValue: number) => Promise<void> | void;
    })
  | (BaseProps & {
      value: string;
      type: "select";
      options: { value: string; label: string }[];
      onSave: (newValue: string) => Promise<void> | void;
    });

export function EditableCell(props: EditableCellProps) {
  const { className, align = "left", disabled, dataCol } = props;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | number>(props.value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const tdRef = useRef<HTMLTableCellElement>(null);
  const committingRef = useRef(false);
  // Etter commit: hvilken retning skal target-cellen åpnes i. Sett av Tab/Enter
  // i edit-mode (rapid entry — neste celle åpnes direkte). Piltastene fra
  // edit-mode commit'er BARE; de "selecter" target uten å åpne editor.
  const pendingFocusRef = useRef<{ dir: NavDirection; openEditor: boolean } | null>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    setDraft(props.value);
  }, [props.value]);

  const cancel = () => {
    setDraft(props.value);
    setEditing(false);
  };

  /**
   * Etter commit: flytt fokus (og evt. åpne editor) på target-cellen. Vent på
   * rAF så React har rendret nåværende celle som display før vi finner target.
   */
  const consumePendingFocus = () => {
    const pending = pendingFocusRef.current;
    pendingFocusRef.current = null;
    if (!pending) return;
    requestAnimationFrame(() => {
      const target = findNavTarget(tdRef.current, pending.dir);
      if (!target) return;
      target.focus();
      // Auto-åpne target i edit-mode KUN hvis det er en editable celle.
      // NavActionCells (×, +, "Ny timeføring…") ville ellers blitt auto-
      // aktivert via .click() når brukeren tab'er forbi siste editable celle
      // i raden — som ville trigget sletting av raden.
      if (pending.openEditor && target.dataset.editable === "true") {
        target.click();
      }
    });
  };

  const commit = async () => {
    if (committingRef.current) return; // already committing — second call is no-op

    if (props.type === "number" && typeof draft === "number" && isNaN(draft)) {
      pendingFocusRef.current = null; // NaN = avbryt, ingen fokus-flytt
      cancel();
      return;
    }
    if (draft === props.value) {
      setEditing(false);
      consumePendingFocus();
      return;
    }

    committingRef.current = true;
    setSaving(true);
    try {
      await Promise.resolve(
        (props.onSave as (v: string | number) => Promise<void> | void)(draft),
      );
      setEditing(false);
    } catch (err) {
      console.error("EditableCell save failed", err);
    } finally {
      setSaving(false);
      committingRef.current = false;
      consumePendingFocus();
    }
  };

  // Tastatur i EDIT-mode: Tab/Enter commit + flytter + åpner editor (rapid
  // entry), piltastene commit + flytter MEN åpner ikke editor (select-only).
  // Escape avbryter og returnerer fokus til display-TDen.
  const handleEditingKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      pendingFocusRef.current = null;
      cancel();
      requestAnimationFrame(() => tdRef.current?.focus());
      return;
    }

    const isTab = e.key === "Tab";
    const isEnter = e.key === "Enter";
    if (isTab || isEnter) {
      e.preventDefault();
      const dir: NavDirection = isTab
        ? e.shiftKey ? "prev" : "next"
        : e.shiftKey ? "up" : "down";
      pendingFocusRef.current = { dir, openEditor: true };
      // Flytt fokus til target SYNKRONT så browser ikke faller ut av tabellen
      // når input-en unmounter.
      const target = findNavTarget(tdRef.current, dir);
      target?.focus();
      void commit();
      return;
    }
    // Piltastene inne i edit-mode: vi lar tekstmarkør og select-dropdowns
    // håndtere dem nativt. For å navigere mellom celler må brukeren først
    // trykke Escape (eller bruke Tab/Enter for å committe+flytte).
  };

  // Tastatur i SELECTED-mode (TD har fokus, ingen editor): piltastene flytter
  // selection, Enter/F2 åpner editor, printbare tegn åpner editor og pre-
  // fyller. Tab håndteres av native browser hvis vi ikke fanger den, men vi
  // overstyrer for konsistens med piltast-navigasjon.
  const handleSelectedKey = (e: KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === "F2") {
      e.preventDefault();
      setDraft(props.value);
      setEditing(true);
      return;
    }
    if (isPrintableKey(e) && props.type !== "select") {
      // For text/number/date: åpne editor og pre-fyll med tegnet
      e.preventDefault();
      if (props.type === "number") {
        const parsed = parseFloat(e.key);
        setDraft(isNaN(parsed) ? NaN : parsed);
      } else {
        setDraft(e.key);
      }
      setEditing(true);
      return;
    }
    const dir = arrowKeyToDirection(e);
    if (dir) {
      e.preventDefault();
      const target = findNavTarget(tdRef.current, dir);
      target?.focus();
    }
  };

  if (!editing) {
    const display =
      props.type === "select"
        ? props.options.find((o) => o.value === props.value)?.label ?? props.value
        : props.value;
    return (
      <td
        ref={tdRef}
        data-nav-cell={!disabled ? "true" : undefined}
        data-editable={!disabled ? "true" : undefined}
        data-col={dataCol}
        tabIndex={disabled ? undefined : 0}
        className={cn(
          "px-2 py-1 border border-border cursor-text",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset",
          align === "right" && "text-right tabular-nums",
          disabled && "cursor-default text-muted-foreground",
          className,
        )}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={handleSelectedKey}
      >
        {display}
      </td>
    );
  }

  const commonInputClass = cn(
    "w-full h-full border-none bg-white px-2 py-1 text-sm",
    align === "right" && "text-right tabular-nums",
    saving && "opacity-50",
  );

  if (props.type === "select") {
    return (
      <td
        ref={tdRef}
        data-nav-cell="true"
        data-col={dataCol}
        tabIndex={-1}
        className={cn("p-0 border-2 border-primary", className)}
      >
        <select
          ref={inputRef as RefObject<HTMLSelectElement>}
          className={commonInputClass}
          value={draft as string}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            void commit();
          }}
          onKeyDown={handleEditingKey}
        >
          {props.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
    );
  }

  return (
    <td
      ref={tdRef}
      data-nav-cell="true"
      data-col={dataCol}
      tabIndex={-1}
      className={cn("p-0 border-2 border-primary", className)}
    >
      <input
        ref={inputRef as RefObject<HTMLInputElement>}
        type={props.type}
        className={commonInputClass}
        value={
          props.type === "number" && typeof draft === "number" && isNaN(draft)
            ? ""
            : draft
        }
        step={
          props.type === "number" ? (props as { step?: number }).step ?? 0.5 : undefined
        }
        disabled={saving}
        onChange={(e) => {
          const raw = e.target.value;
          if (props.type === "number") {
            if (raw === "") {
              setDraft(NaN);
            } else {
              const parsed = parseFloat(raw);
              setDraft(isNaN(parsed) ? NaN : parsed);
            }
          } else {
            setDraft(raw);
          }
        }}
        onBlur={() => {
          void commit();
        }}
        onKeyDown={handleEditingKey}
      />
    </td>
  );
}
